import React, { useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function ProtectedRoute({ children, requiredPerm }) {
  const { user, hasPerm } = useAuth();
  const location = useLocation();

  // Preserves the pre-redirect URL so /login can bounce us back after auth.
  // Memoized to keep object identity stable across renders.
  const redirectState = useMemo(() => ({ from: location }), [location]);

  if (user === null) {
    return (
      <div data-testid="auth-loading" className="min-h-screen flex items-center justify-center">
        <div className="font-mono uppercase tracking-widest text-xs">LOADING…</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={redirectState} replace />;
  }
  // Must run before the role checks below, or a buyer/vendor bounces between /buyer and /profile forever
  // (blank screen). An admin viewing as someone never gets that person's change-password screen.
  if (user.must_change_password && !user.impersonated_by) {
    return location.pathname.startsWith("/profile") ? children : <Navigate to="/profile" replace />;
  }
  // Iter-30: buyers only see /buyer (and their Guide). Any attempt to visit an admin surface
  // bounces them back to their read-only dashboard.
  const onHelp = location.pathname.replace(/\/+$/, "") === "/help";
  if (user.role === "buyer" && location.pathname !== "/buyer" && !onHelp) {
    return <Navigate to="/buyer" replace />;
  }
  // Iter-30.6: same rule for vendors — read-only /vendor only.
  if (user.role === "vendor" && !["/vendor", "/postbacks"].includes(location.pathname.replace(/\/+$/, "")) && !onHelp) {
    return <Navigate to="/vendor" replace />;
  }
  // Iter-45: media buyers are scoped to landing pages, pixels, and their
  // masked attribution dashboard. Any other admin surface bounces them
  // back to /admin/landing-pages so they can't stumble into PII/margins.
  if (user.role === "media_buyer") {
    const allowed = [
      "/admin/landing-pages",
      "/admin/pixels",
      "/media-buyer/attribution",
      "/media-buyer/leads",
      "/results",
      "/media-buyer/custom-tracking",
      "/postbacks",
      "/profile",
      "/timeline",
      "/help",
    ];
    const ok = allowed.some(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
    );
    // Custom landing page editor is pixel-only for media buyers — the
    // page enforces read-only for non-admin fields; allow the route.
    const isCustomLpEditor =
      location.pathname.startsWith("/admin/landing-pages/custom/") &&
      !location.pathname.endsWith("/new");
    if (!ok && !isCustomLpEditor) {
      return <Navigate to="/admin/landing-pages" replace />;
    }
  }
  if (requiredPerm && !hasPerm(requiredPerm)) {
    return (
      <div data-testid="permission-denied" className="min-h-screen flex flex-col items-center justify-center gap-2">
        <div className="font-chivo text-3xl font-black uppercase tracking-tighter">Denied.</div>
        <div className="label-mono">Missing permission: {requiredPerm}</div>
      </div>
    );
  }
  return children;
}
